import { useTranslation } from 'react-i18next';
import { ShieldX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isPermissionDenied } from '@/lib/apiError';

import type { JSX } from "react";

interface Props {
  /** Error that triggered the forbidden state. Used to surface a per-resource
 * message when available; falls back to the generic copy. Accepts any
 * shape — RTK Query's `unknown`, axios responses, plain Errors, etc. */
  error?: unknown;
  /** Layout variant:
 * - `page`: full-height card filling the viewport content area
 * - `section`: inline card that fits inside an existing layout */
  variant?: 'page' | 'section';
  className?: string;
  'data-testid'?: string;
}

function extractDetail(error: unknown): string | null {
  if (!error) return null;
  const sample = Array.isArray(error) ? error.find(isPermissionDenied) : error;
  if (!sample || typeof sample !== 'object') return null;
  // Common shapes: { data: { message } } (RTK Query) and { response: { data: { message } } }.
  const data = (sample as { data?: { message?: string } }).data;
  if (data?.message) return data.message;
  const responseData = (sample as { response?: { data?: { message?: string } } }).response?.data;
  if (responseData?.message) return responseData.message;
  return null;
}

/**
 * Replacement for an empty/broken UI when an endpoint returns 403.
 * Mirrors `/portal/src/app/components/ForbiddenPlaceholder/index.js`.
 * The shadcn-skinned equivalent uses a neutral muted card with a shield icon
 * + headline + body. Per, `variant=page` is used when the entire
 * page is denied and `variant=section` for inline panels inside an
 * otherwise-rendered surface.
 */
export function ForbiddenPlaceholder({
  error,
  variant = 'section',
  className,
  'data-testid': testId,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const detail = extractDetail(error);
  const headline = t('common:forbidden.title', {
    defaultValue: 'You don’t have permission to view this',
  });
  const fallback = t('common:forbidden.body', {
    defaultValue:
      'Contact your account administrator if you believe this is a mistake.',
  });
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        variant === 'page' ? 'min-h-[60vh] p-8' : 'py-12 px-4',
        className,
      )}
      data-testid={testId ?? 'forbidden-placeholder'}
    >
      <div
        className={cn(
          'flex max-w-md flex-col items-center gap-3 rounded-lg border bg-muted/30 px-6 py-8 text-center',
          variant === 'page' && 'shadow-xs',
        )}
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive"
          aria-hidden
        >
          <ShieldX className="h-6 w-6" />
        </span>
        <h2 className="text-base font-semibold">{headline}</h2>
        <p className="text-sm text-muted-foreground">{detail ?? fallback}</p>
      </div>
    </div>
  );
}
